import type { ReactNode } from "react";
import { useCallback, useMemo, useState } from "react";

import { SidebarContext } from "./SidebarContext";
import type { SidebarResource } from "./types";

type Props = {
  children: ReactNode;
};

export const SidebarContextProvider = ({ children }: Props) => {
  const [mainIsOpen, setMainIsOpen] = useState(false);
  const [popOutIsOpen, setPopOutIsOpen] = useState(false);

  const [panel, setPanel] = useState<ReactNode | undefined>();
  const [name, setName] = useState<SidebarResource["name"] | undefined>();

  const [selectedResourceName, setSelectedResourceName] = useState<string | undefined>();

  const main = useMemo(
    () => ({
      isOpen: mainIsOpen,
      selectedResourceName,
      openPanel: () => setMainIsOpen(true),
      closePanel: () => setMainIsOpen(false),
    }),
    [mainIsOpen, selectedResourceName],
  );

  const getPanel = useCallback(
    (resources: SidebarResource[]) => panel ?? resources.find((resource) => resource.name === name)?.panel,
    [panel, name],
  );

  const handleSetPanelByName = useCallback((newName: SidebarResource["name"]) => {
    setPanel(undefined);
    setName(newName);
  }, []);

  const handleSetPanelDangerously = useCallback((newPanel: ReactNode) => {
    setName(undefined);
    setPanel(newPanel);
  }, []);

  const popOut = useMemo(
    () => ({
      isOpen: popOutIsOpen,
      openPanel: () => setPopOutIsOpen(true),
      closePanel: () => setPopOutIsOpen(false),
      setPanelByName: handleSetPanelByName,
      setPanelDangerously: handleSetPanelDangerously,
      panelName: name,
      getPanel,
    }),
    [popOutIsOpen, handleSetPanelByName, handleSetPanelDangerously, getPanel, name],
  );

  const selectMainPanelByName = useCallback(
    (newName: string) => {
      if (newName === selectedResourceName) {
        if (main.isOpen) {
          main.closePanel();
        } else {
          main.openPanel();
        }

        if (popOut.isOpen) {
          popOut.closePanel();
        }
      } else {
        setSelectedResourceName(newName);

        if (popOut.isOpen) {
          popOut.closePanel();
        }

        if (!main.isOpen) {
          main.openPanel();
        }
      }
    },
    [main, popOut, selectedResourceName],
  );

  return <SidebarContext.Provider value={{ main, popOut, selectMainPanelByName }}>{children}</SidebarContext.Provider>;
};
